import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchApi } from '@/common/lib/fetchApi'

export type NotificationEventType =
  | 'timesheet_window_open'
  | 'timesheet_due'
  | 'timesheet_overdue'
  | 'approval_needed'
  | 'approval_overdue'
  | 'deadline_urgent'

export type NotificationChannel = 'email' | 'in_app'
export type NotificationStatus = 'sent' | 'failed'

export interface ScopedPayPeriod {
  id: number
  start_date: string
  end_date: string
}

export interface NotificationLogGroup {
  event_type: NotificationEventType
  pay_period_id: number | null
  send_date: string
  channel: NotificationChannel
  sent_count: number
  failed_count: number
  recipient_count: number
}

export interface NotificationActivity {
  pay_period: ScopedPayPeriod
  groups: NotificationLogGroup[]
}

export interface NotificationLogEntry {
  id: number
  recipient_employee_id: number
  recipient_name: string | null
  recipient_email: string | null
  status: NotificationStatus
  sent_at: string
  error_message: string | null
}

export interface NotificationRule {
  id: number
  event_type: NotificationEventType
  trigger_type: string
  days_offset: number | null
  repeat_interval_days: number | null
  enabled: boolean
  email_template_key: string
  subject_template: string
}

/** Human-readable labels for notification event types. */
export const EVENT_TYPE_LABELS: Record<NotificationEventType, string> = {
  timesheet_window_open: 'Timesheet window open',
  timesheet_due: 'Timesheet due',
  timesheet_overdue: 'Timesheet overdue',
  approval_needed: 'Approval needed',
  approval_overdue: 'Approval overdue',
  deadline_urgent: 'Deadline urgent',
}

export function useNotificationActivity() {
  return useQuery({
    queryKey: ['admin', 'notification-activity'],
    queryFn: () => fetchApi<NotificationActivity>('/api/org/notification-logs'),
  })
}

export interface EntriesParams {
  event_type: NotificationEventType
  pay_period_id: number
  date: string
  channel: NotificationChannel
}

export function useNotificationEntries(params: EntriesParams | null) {
  return useQuery({
    enabled: params != null,
    queryKey: ['admin', 'notification-entries', params],
    queryFn: () => {
      const qs = new URLSearchParams({
        event_type: params!.event_type,
        pay_period_id: String(params!.pay_period_id),
        date: params!.date,
        channel: params!.channel,
      }).toString()
      return fetchApi<NotificationLogEntry[]>(`/api/org/notification-logs/entries?${qs}`)
    },
  })
}

export function useNotificationRules() {
  return useQuery({
    queryKey: ['admin', 'notification-rules'],
    queryFn: () => fetchApi<NotificationRule[]>('/api/org/notification-rules'),
  })
}

export function useToggleNotificationRule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      fetchApi<NotificationRule>(`/api/org/notification-rules/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'notification-rules'] })
    },
  })
}
